import { useEffect, useState, useCallback } from "react";
import { MapPin, Trash2, Plus, Gift, Copy, Share2, Wallet, ShieldCheck, BadgeCheck, Bell, Mail } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatFCFA } from "@/lib/format";
import { Loader, StarRating } from "@/components/common";
import { SelectField, TextField } from "@/components/Modal";
import PushButton from "@/components/PushButton";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [zones, setZones] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ label: "Domicile", zone: "", quartier: "", address: "", phone: "", is_default: false });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const load = useCallback(async () => {
    const [a, z, r] = await Promise.all([api.get("/addresses"), api.get("/zones"), api.get("/reviews")]);
    setAddresses(a.data); setZones(z.data); setReviews(r.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      await api.post("/addresses", f);
      toast.success("Adresse ajoutée"); setShowForm(false);
      setF({ label: "Domicile", zone: "", quartier: "", address: "", phone: "", is_default: false });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const delAddress = async (aid) => { await api.delete(`/addresses/${aid}`); load(); };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-extrabold text-[#0F172A] mb-5">Mon profil</h1>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-display text-2xl font-bold">
            {(user.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-display text-xl font-bold">{user.name}</p>
            {user.email && (
              <p className="text-sm text-slate-500 break-all flex items-center flex-wrap gap-1.5">{user.email}
                {user.email_verified
                  ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full" data-testid="email-verified-badge"><BadgeCheck className="w-3 h-3" /> Vérifié</span>
                  : <span className="inline-flex items-center text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid="email-unverified-badge">Non vérifié</span>}
              </p>
            )}
            {user.phone && (
              <p className="text-sm text-slate-500 flex items-center flex-wrap gap-1.5">{user.phone}
                {user.phone_verified
                  ? <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full" data-testid="phone-verified-badge-header"><BadgeCheck className="w-3 h-3" /> Vérifié</span>
                  : <span className="inline-flex items-center text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid="phone-unverified-badge">Non vérifié</span>}
              </p>
            )}
          </div>
          <div className="w-full sm:w-auto sm:ml-auto"><PushButton /></div>
        </div>
      </div>

      <ReferralSection />

      <EmailVerification user={user} />
      <PhoneVerification user={user} />
      <NotifPrefs />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold">Mes adresses</h2>
          <button onClick={() => setShowForm((s) => !s)} className="text-sm font-semibold text-[#C85A32] flex items-center gap-1" data-testid="add-address-toggle"><Plus className="w-4 h-4" /> Ajouter</button>
        </div>
        {showForm && (
          <form onSubmit={addAddress} className="space-y-3 mb-4 bg-slate-50 rounded-lg p-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Libellé" value={f.label} onChange={set("label")} testid="addr-label" />
              <SelectField label="Zone" value={f.zone} onChange={set("zone")} testid="addr-zone" options={zones.map((z) => ({ value: z.name, label: z.name }))} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Quartier" value={f.quartier} onChange={set("quartier")} testid="addr-quartier" required={false} />
              <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="addr-phone" required={false} />
            </div>
            <TextField label="Adresse complète" value={f.address} onChange={set("address")} testid="addr-address" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} onChange={(e) => set("is_default")(e.target.checked)} data-testid="addr-default" /> Adresse par défaut</label>
            <button className="bg-[#C85A32] text-white font-bold px-4 py-2 rounded-lg text-sm" data-testid="addr-save">Enregistrer</button>
          </form>
        )}
        {addresses.length === 0 ? <p className="text-sm text-slate-400">Aucune adresse enregistrée.</p> : (
          <div className="space-y-2">
            {addresses.map((a) => (
              <div key={a.id} className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg" data-testid={`address-${a.id}`}>
                <MapPin className="w-4 h-4 text-[#C85A32] mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">{a.label} {a.is_default && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">par défaut</span>}</p>
                  <p className="text-xs text-slate-500">{a.zone} · {a.address} {a.phone && `· ${a.phone}`}</p>
                </div>
                <button onClick={() => delAddress(a.id)} className="text-red-500 p-1" data-testid={`address-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-display text-xl font-bold mb-3">Mes avis</h2>
        {reviews.length === 0 ? <p className="text-sm text-slate-400">Vous n'avez pas encore laissé d'avis.</p> : (
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.id} className="border-b last:border-0 pb-3">
                <div className="flex items-center gap-2">
                  <StarRating value={r.rating} readOnly size={16} />
                  <span className="text-xs text-slate-400">{r.target_type === "delivery" ? "Livraison" : "Installation"} · {formatDate(r.created_at)}</span>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailVerification({ user }) {
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [step, setStep] = useState(user.email_verified ? "done" : "idle");
  const [dev, setDev] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user.email) return null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-otp", { channel: "email" });
      setDev(data.dev_code || ""); setStep("code"); toast.success(`Code envoyé à ${user.email}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", { channel: "email", code });
      setStep("done"); toast.success("E-mail vérifié ✅"); refresh?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="email-verification">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-[#C85A32]" />
        <h2 className="font-display text-xl font-bold">Vérification de l'e-mail</h2>
        {step === "done" && <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full" data-testid="email-verified-badge-card"><BadgeCheck className="w-3.5 h-3.5" /> Vérifié</span>}
      </div>
      {step === "done" ? (
        <p className="text-sm text-slate-500">Votre e-mail <strong>{user.email}</strong> est vérifié.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Confirmez votre adresse <strong>{user.email}</strong> pour sécuriser votre compte.</p>
          {step === "code" && dev && <p className="text-[11px] text-amber-600">Code (démo — envoi non délivré en préversion) : <strong className="font-num">{dev}</strong></p>}
          {step !== "code" ? (
            <button onClick={send} disabled={busy} className="bg-[#C85A32] text-white font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="email-send-otp">{busy ? "Envoi…" : "Recevoir un code"}</button>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1"><TextField label="Code reçu" value={code} onChange={setCode} testid="email-code" placeholder="123456" /></div>
              <button onClick={verify} disabled={busy || !code} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="email-verify-btn">{busy ? "…" : "Vérifier"}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhoneVerification({ user }) {
  const { refresh } = useAuth();
  const [phone, setPhone] = useState(user.phone || "");
  const [channel, setChannel] = useState("sms");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(user.phone_verified ? "done" : "idle");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/phone/send-otp", { phone, channel });
      if (data.ok) { setStep("code"); toast.success(`Code envoyé à ${data.to}`); }
      else toast.error(data.error || "Échec de l'envoi du code");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/phone/verify", { phone, code });
      if (!data.ok) toast.error(data.error || "Erreur de vérification");
      else if (data.valid) { setStep("done"); toast.success("Numéro vérifié ✅"); refresh?.(); }
      else toast.error("Code invalide ❌");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="phone-verification">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#C85A32]" />
        <h2 className="font-display text-xl font-bold">Vérification du téléphone</h2>
        {step === "done" && <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full" data-testid="phone-verified-badge"><BadgeCheck className="w-3.5 h-3.5" /> Vérifié</span>}
      </div>
      {step === "done" ? (
        <p className="text-sm text-slate-500">Votre numéro <strong>{user.phone}</strong> est vérifié. Vous recevrez vos notifications SMS/WhatsApp de façon fiable.</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Confirmez votre numéro pour recevoir les notifications SMS/WhatsApp de vos commandes.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Numéro de téléphone" value={phone} onChange={setPhone} testid="phone-input" placeholder="+228 90 00 00 00" />
            <SelectField label="Recevoir le code par" value={channel} onChange={setChannel} testid="phone-channel" options={[{ value: "sms", label: "SMS" }, { value: "whatsapp", label: "WhatsApp" }]} />
          </div>
          {step !== "code" ? (
            <button onClick={send} disabled={busy || !phone} className="bg-[#C85A32] text-white font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="phone-send-otp">{busy ? "Envoi…" : "Recevoir un code"}</button>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1"><TextField label="Code reçu" value={code} onChange={setCode} testid="phone-code" placeholder="123456" /></div>
              <button onClick={verify} disabled={busy || !code} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="phone-verify-btn">{busy ? "…" : "Vérifier"}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PREF_LABELS = [["email", "Email"], ["sms", "SMS"], ["whatsapp", "WhatsApp"], ["push", "Notifications push"]];

function NotifPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/me/notif-prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs(false)); }, []);
  if (prefs === null) return null;
  if (prefs === false) return null;
  const save = async () => {
    setBusy(true);
    try { await api.put("/me/notif-prefs", prefs); toast.success("Préférences enregistrées"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="notif-prefs">
      <div className="flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-xl font-bold">Mes préférences de notification</h2></div>
      <p className="text-sm text-slate-500 mb-3">Choisissez comment vous souhaitez être informé (commandes, paiements, livraisons, devis).</p>
      <div className="divide-y divide-slate-100">
        {PREF_LABELS.map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 py-2.5 cursor-pointer" data-testid={`pref-${k}`}>
            <input type="checkbox" checked={!!prefs[k]} onChange={(e) => setPrefs((s) => ({ ...s, [k]: e.target.checked }))} className="w-4 h-4" />
            <span className="text-sm font-semibold text-slate-800">{label}</span>
          </label>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="mt-3 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="notif-prefs-save">{busy ? "Enregistrement…" : "Enregistrer mes préférences"}</button>
    </div>
  );
}

function ReferralSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/client-referral").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  if (!data) return null;
  if (data.enabled === false) return null;
  const link = `${window.location.origin}/rc/${data.code}`;
  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };
  const share = () => {
    const text = `Je commande mes matériaux sur TOGOBATI ! Utilise mon lien et profite de ${formatFCFA(data.referee_reward)} de réduction sur ta 1re commande : ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5" data-testid="client-referral-section">
      <div className="flex items-center gap-2 mb-3"><Gift className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-xl font-bold">Parrainer & Gagner</h2></div>

      <div className="rounded-xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-center">
        <div className="bg-white p-2.5 rounded-lg shrink-0"><QRCodeCanvas value={link} size={110} level="M" /></div>
        <div className="flex-1 w-full min-w-0">
          <p className="text-xs text-slate-300">Gagnez <strong className="text-[#E08B00]">{formatFCFA(data.referrer_reward)}</strong> de crédit par filleul. Votre filleul reçoit <strong className="text-[#E08B00]">{formatFCFA(data.referee_reward)}</strong> de réduction sur sa 1re commande.</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-lg font-bold" data-testid="client-ref-code">{data.code}</span>
            <button onClick={() => copy(data.code, "Code copié")} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"><Copy className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-2 mt-2 bg-black/20 rounded-lg px-3 py-2">
            <span className="text-sm truncate flex-1" data-testid="client-ref-link">{link}</span>
            <button onClick={() => copy(link, "Lien copié")} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25 shrink-0"><Copy className="w-4 h-4" /></button>
          </div>
          <button onClick={share} className="mt-2 text-xs font-bold bg-white text-[#0F172A] px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="client-ref-share"><Share2 className="w-4 h-4" /> Partager sur WhatsApp</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center" data-testid="client-ref-clicks">
          <p className="font-display text-lg font-extrabold text-[#0F172A]">{data.clicks || 0}</p>
          <p className="text-xs text-slate-500">Clics sur mon lien</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center" data-testid="client-ref-count">
          <p className="font-display text-lg font-extrabold text-[#0F172A]">{data.referrals.length}</p>
          <p className="text-xs text-slate-500">Filleuls</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <Wallet className="w-5 h-5 text-[#C85A32]" />
        <span className="text-sm text-slate-700">Mon crédit disponible :</span>
        <span className="ml-auto font-display text-lg font-extrabold text-[#C85A32]" data-testid="wallet-balance">{formatFCFA(data.wallet_balance)}</span>
      </div>

      {data.referrals.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-slate-700 mb-2">Mes filleuls</p>
          <div className="space-y-1.5">
            {data.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2" data-testid={`client-ref-row-${r.id}`}>
                <span>{r.referee_name}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "rewarded" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                  {r.status === "rewarded" ? `+${formatFCFA(r.referrer_reward)}` : "En attente 1re commande"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
